import { refreshChatAvatar } from "./app-chat.ts";
import { syncUrlWithSessionKey } from "./app-settings.ts";
import { resolveHistoryAgentId } from "./chat/chat-history-catalog.ts";
import {
  archiveSessionKeys,
  canArchiveSessionKey,
  closeArchivedTabKeys,
  unarchiveSessionKeys,
} from "./chat/chat-session-archive.ts";
import { nextChatTabLabel, normalizeChatOpenTabs, removeChatOpenTab } from "./chat/chat-tabs.ts";
import { clearChatWaitState } from "./chat/chat-wait-ticker.ts";
import { resolveMainSessionKey } from "./chat/resolve-main-session.ts";
import { resolveAgentIdForSession } from "./chat/resolve-session-agent.ts";
import { resolveAgentMainSessionKey } from "./chat/session-catalog.ts";
import {
  restoreChatSubagentsForSession,
  stashChatSubagentsForSession,
} from "./chat/subagents-by-session.ts";
import { loadAssistantIdentity } from "./controllers/assistant-identity.ts";
import { forkSession, type SessionsForkResult } from "./controllers/sessions.ts";
import type { GatewayHelloOk } from "./gateway.ts";
import type { UiSettings } from "./storage.ts";
import { idbDeleteDraft, idbSaveDraft } from "./storage/idb-session-cache.ts";
import type { SessionsListResult } from "./types.ts";
import type { ChatAttachment, ChatQueueItem, ChatSubagentCard } from "./ui-types.ts";
import type { AgentRosterItem } from "./ui-types.ts";

type DraftEntry = { message: string; attachments: ChatAttachment[] };

export type ChatTabsHost = {
  sessionKey: string;
  settings: UiSettings;
  hello: GatewayHelloOk | null;
  sessionsResult: SessionsListResult | null;
  sessionsError: string | null;
  lastError: string | null;
  chatMessage: string;
  chatAttachments: ChatAttachment[];
  chatStream: string | null;
  chatStreamStartedAt: number | null;
  chatRunId: string | null;
  chatQueue: ChatQueueItem[];
  chatSubagents: ChatSubagentCard[];
  chatSubagentExpanded: Record<string, boolean>;
  // JAISIU-XXXX — see app.ts; reset alongside chatSubagentExpanded on
  // chat/session reset so finished-row expansion never carries over.
  chatSubagentTerminalOpen: Record<string, boolean>;
  chatSubagentFilter: "all" | "running";
  agentRoster: AgentRosterItem[];
  /**
   * Resident per-session transcripts. Optional so tests can pass a minimal
   * host; `closeChatTab` deletes the closed key so a full transcript is not
   * pinned for the life of the page.
   */
  chatMessagesBySession?: Map<string, unknown[]>;
  applySettings: (next: UiSettings) => void;
  resetToolStream: () => void;
  resetChatScroll: () => void;
  handleSendChat: (messageOverride?: string, opts?: { restoreDraft?: boolean }) => Promise<void>;
  client: { request: (...args: unknown[]) => Promise<unknown> } | null;
  connected: boolean;
  /**
   * Full session switch including transcript cache + `chat.history`.
   * Tab helpers call this so sidebar / close / new-tab paths cannot leave
   * the previous chat's messages painted on the new tab.
   */
  activateChatSessionHost?: (key: string) => void;
};

export function getChatOpenTabs(host: ChatTabsHost): string[] {
  return normalizeChatOpenTabs(host.settings.chatOpenTabs, host.sessionKey);
}

export function ensureChatTabsInitialized(host: ChatTabsHost) {
  const tabs = normalizeChatOpenTabs(host.settings.chatOpenTabs, host.sessionKey);
  if (
    tabs.join("\0") !== host.settings.chatOpenTabs.join("\0") ||
    !tabs.includes(host.sessionKey)
  ) {
    host.applySettings({ ...host.settings, chatOpenTabs: tabs });
  }
}

function stashDraft(host: ChatTabsHost, cache: Map<string, DraftEntry>) {
  const key = host.sessionKey.trim();
  if (!key) {
    return;
  }
  const attachments = host.chatAttachments.map((attachment) => ({ ...attachment }));
  cache.set(key, {
    message: host.chatMessage,
    attachments,
  });
  // JAISIU-3320 / Pillar I — write-through to IndexedDB so the draft
  // survives a hard reload. Fire-and-forget; the in-memory Map is the
  // source of truth for live reads. No-op when IDB is unavailable.
  void idbSaveDraft(key, {
    message: host.chatMessage,
    attachments,
    updatedAt: Date.now(),
  });
}

function restoreDraft(host: ChatTabsHost, cache: Map<string, DraftEntry>, key: string) {
  const cached = cache.get(key);
  host.chatMessage = cached?.message ?? "";
  host.chatAttachments = cached?.attachments ?? [];
}

function clearTransientChat(host: ChatTabsHost) {
  // Use the helper so the live-wait ticker interval is torn down
  // synchronously — Lit's @state() change detection doesn't fire
  // `updated()` immediately, so a direct `chatRunId = null` write here
  // leaves the setInterval running until the next render lands, which
  // paints a stuck "waiting for answer" bubble with stale elapsed time.
  clearChatWaitState(host as unknown as Parameters<typeof clearChatWaitState>[0]);
  host.chatQueue = [];
  host.chatSubagents = [];
  host.chatSubagentExpanded = {};
  // JAISIU-XXXX — also clear the terminal-open flags so a new chat
  // starts with no finished runs lingering in the expanded state.
  host.chatSubagentTerminalOpen = {};
  host.chatSubagentFilter = "all";
  host.agentRoster = [];
  host.resetToolStream();
  host.resetChatScroll();
}

/**
 * Decide which transcript to render immediately after switching chat
 * sessions. Hosts keep an in-memory cache (`chatMessagesBySession`)
 * keyed by session key — the async `chat.history` round-trip that
 * follows this decision typically takes tens of milliseconds, so we
 * must render *something* synchronously to avoid a visible flash.
 *
 * Two requirements drive the three branches below:
 *
 *  1. Returning the previous session's messages is wrong: it makes
 *     the renderer briefly paint chat-A's transcript while the user
 *     is looking at chat-B's tab (the exact symptom operators report
 *     as "messages from another chat appear when I switch tabs").
 *  2. Returning the new session's cached messages is right when
 *     we've already loaded it once this session lifetime; dropping
 *     the cache would cause a visible re-load on every re-activate.
 *
 * The host's async `loadChatHistory` is responsible for fetching and
 * overwriting `chatMessages` once the server reply lands; this helper
 * only governs the synchronous paint state.
 */
export function selectChatMessagesForSession(params: {
  prevSessionKey: string;
  nextSessionKey: string;
  prevMessages: readonly unknown[];
  cache: ReadonlyMap<string, readonly unknown[]>;
}): { messages: readonly unknown[]; loading: boolean; warm: boolean } {
  const trimmed = params.nextSessionKey.trim();
  const prev = params.prevSessionKey.trim();
  if (trimmed && trimmed !== prev) {
    const cached = params.cache.get(trimmed);
    if (Array.isArray(cached) && cached.length > 0) {
      return { messages: cached, loading: false, warm: true };
    }
    return { messages: [], loading: true, warm: false };
  }
  return { messages: params.prevMessages, loading: false, warm: true };
}

function switchChatSession(host: ChatTabsHost, cache: Map<string, DraftEntry>, next: string) {
  if (host.activateChatSessionHost) {
    host.activateChatSessionHost(next);
    return;
  }
  activateChatSession(host, cache, next);
}

export function activateChatSession(
  host: ChatTabsHost,
  cache: Map<string, DraftEntry>,
  next: string,
) {
  const trimmed = next.trim();
  if (!trimmed || trimmed === host.sessionKey) {
    return;
  }
  stashDraft(host, cache);
  stashChatSubagentsForSession(host, host.sessionKey, host.chatSubagents ?? []);
  clearTransientChat(host);
  host.sessionKey = trimmed;
  restoreDraft(host, cache, trimmed);
  host.chatSubagents = restoreChatSubagentsForSession(host, trimmed, host.sessionsResult);
  const tabs = normalizeChatOpenTabs(host.settings.chatOpenTabs, trimmed);
  host.applySettings({
    ...host.settings,
    sessionKey: trimmed,
    lastActiveSessionKey: trimmed,
    chatOpenTabs: tabs,
  });
  void loadAssistantIdentity(host as unknown as Parameters<typeof loadAssistantIdentity>[0]);
  syncUrlWithSessionKey(
    host as unknown as Parameters<typeof syncUrlWithSessionKey>[0],
    trimmed,
    true,
  );
  void refreshChatAvatar(host as unknown as Parameters<typeof refreshChatAvatar>[0]);
  // Refetch the full chat-history catalog after switching. We must NOT use
  // `loadSessions({ activeMinutes: 120 })` here — that filter would
  // overwrite `state.sessionsResult` (the canonical chat-history list)
  // with only the last 2 hours of sessions, which makes the
  // chat-history sidebar lose every older row the moment the user clicks
  // on it. The chat-tabs bar does not need a recency-filtered
  // `sessionsResult`; it renders from `state.chatOpenTabs`, not from the
  // catalog. Subagent restoration below needs the full list to find
  // every subagent of the freshly-activated session.
  //
  // The helper `refreshChatHistorySessions` is the single, canonical
  // chat-history fetch (no `activeMinutes`, no `agentId` filter — see
  // app.ts). It is safe to call from any chat-tab path.
  const refreshHost = host as unknown as {
    refreshChatHistorySessions?: () => Promise<void>;
  };
  if (typeof refreshHost.refreshChatHistorySessions === "function") {
    void refreshHost.refreshChatHistorySessions().then(() => {
      host.chatSubagents = restoreChatSubagentsForSession(host, trimmed, host.sessionsResult);
    });
  }
}

export function openChatTab(host: ChatTabsHost, cache: Map<string, DraftEntry>, key: string) {
  const trimmed = key.trim();
  if (!trimmed) {
    return;
  }
  const tabs = normalizeChatOpenTabs([...host.settings.chatOpenTabs, trimmed], trimmed);
  host.applySettings({ ...host.settings, chatOpenTabs: tabs });
  switchChatSession(host, cache, trimmed);
}

export function closeChatTab(host: ChatTabsHost, cache: Map<string, DraftEntry>, key: string) {
  const currentTabs = getChatOpenTabs(host);
  const idx = currentTabs.indexOf(key);
  const fallback = currentTabs[0] === key ? (currentTabs[1] ?? host.sessionKey) : host.sessionKey;
  const tabs = removeChatOpenTab(currentTabs, key, fallback);
  cache.delete(key);
  // JAISIU-3320 / Pillar I — drop the IDB row alongside the in-memory
  // cache so a closed tab does not resurrect on reload.
  void idbDeleteDraft(key);
  // Free the resident transcript too. Closing a tab used to drop only the
  // draft, leaving a full drained transcript (with any media blocks) in
  // `chatMessagesBySession` for the life of the page. The cache is LRU-bounded
  // now, but an explicitly closed tab should not sit in it at all.
  host.chatMessagesBySession?.delete(key);

  if (key === host.sessionKey) {
    const nextActive = tabs[Math.min(Math.max(0, idx - 1), tabs.length - 1)] ?? tabs[0]!;
    host.applySettings({ ...host.settings, chatOpenTabs: tabs });
    switchChatSession(host, cache, nextActive);
    return;
  }
  host.applySettings({ ...host.settings, chatOpenTabs: tabs });
}

export function archiveChatSessionInUi(
  host: ChatTabsHost,
  cache: Map<string, DraftEntry>,
  sessionKey: string,
) {
  const key = sessionKey.trim();
  if (!key || !canArchiveSessionKey(key)) {
    return;
  }
  const main =
    resolveMainSessionKey(host.hello, host.sessionsResult) ??
    resolveAgentMainSessionKey(
      resolveHistoryAgentId(host.sessionKey),
      host.sessionsResult,
      host.hello,
    ) ??
    host.sessionKey;
  const nextSettings = archiveSessionKeys(host.settings, [key]);
  const tabs = closeArchivedTabKeys(getChatOpenTabs(host), [key], main);
  host.applySettings({ ...nextSettings, chatOpenTabs: tabs });
  // Archiving is a deliberate end-of-life for the session; drop its resident
  // transcript and draft rather than waiting for the LRU bound to notice.
  host.chatMessagesBySession?.delete(key);
  cache.delete(key);
  void idbDeleteDraft(key);
  if (key === host.sessionKey) {
    const nextActive = tabs.includes(main) ? main : (tabs[0] ?? main);
    switchChatSession(host, cache, nextActive);
  }
}

export function restoreArchivedChatSession(host: ChatTabsHost, sessionKey: string) {
  const key = sessionKey.trim();
  if (!key) {
    return;
  }
  host.applySettings(unarchiveSessionKeys(host.settings, [key]));
}

/** Open a session from the chat-history sidebar.
 *  Uses `switchChatSession` so the host's full `activateChatSession` path
 *  (transcript cache + `chat.history`) runs. Isolated tab-host tests without
 *  `activateChatSessionHost` fall back to tab-layer activate only. */
export function openChatHistorySession(
  host: ChatTabsHost,
  cache: Map<string, DraftEntry>,
  sessionKey: string,
) {
  const key = sessionKey.trim();
  if (!key) {
    return;
  }
  host.applySettings(unarchiveSessionKeys(host.settings, [key]));
  switchChatSession(host, cache, key);
}

/** Archive current tab (if not main), then start a new empty chat. */
export async function handleArchiveAndNewChat(host: ChatTabsHost, cache: Map<string, DraftEntry>) {
  const current = host.sessionKey.trim();
  if (canArchiveSessionKey(current)) {
    archiveChatSessionInUi(host, cache, current);
  }
  await handleNewEmptyChatTab(host, cache);
}

/**
 * New empty session: fork a genuinely NEW session key from the agent's
 * main session WITHOUT copying the transcript.
 *
 * JAISIU-new-chat-must-fork: this used to take a "guard" branch that
 * reused the agent's main `sessionKey` and sent an in-place `/new`
 * reset instead of allocating a new key. The observable effect was that
 * the operator clicked "New chat" and stayed in the SAME chat: the
 * sidebar still showed the single `agent:main:main` row, the tab
 * header did not change, and `sessions.reset` even PRESERVES the
 * `sessionId` for the main session (GAAL-SESS-CACHE-001), so nothing
 * about the chat identity changed at all.
 *
 * "New chat" is an EXPLICIT user request for a distinct chat. The
 * session-stickiness policy (docs/policy/single-session-stickiness.md)
 * exists to stop *automatic* fan-out from duplicating the main session;
 * it does not apply to a user explicitly asking for a new chat. The
 * policy is amended accordingly — see the "New chat" carve-out in that
 * document.
 *
 * A fork (not a reset) is the right primitive here: it mints a new
 * `sessionKey` (`agent:<agentId>:fork:<uuid>`) with a fresh
 * `sessionId` and an EMPTY transcript, so the operator genuinely lands
 * in a new chat and the old one stays in the history sidebar.
 */
export async function handleNewEmptyChatTab(host: ChatTabsHost, cache: Map<string, DraftEntry>) {
  const agentId = resolveAgentIdForSession(host.sessionKey);
  const resolvedMain = resolveAgentMainSessionKey(agentId, host.sessionsResult, host.hello);
  // JAISIU-3109: when the user is sitting in a subagent whose owning agent
  // has no chat history (e.g. `jaisiu-code-fix-agent` with only bare
  // subagents), `resolveAgentMainSessionKey` returns the synthetic
  // `agent:<X>:main` placeholder that doesn't exist on disk.
  // `sessions.fork` then rejects with NOT_FOUND and the "New chat" button
  // silently fails — breaking the chat-session-tabs + sidebar "New chat".
  // Fall back to the operator's `agent:main:main` (or whatever the
  // gateway reports as the global main via hello.sessions.main) so the
  // operator always gets a fresh tab.
  //
  // The CURRENT session is the last-resort source: if the agent has no
  // main session at all but the operator is sitting in a live chat, that
  // chat is a perfectly good fork source (it exists on disk). Without it,
  // "New chat" would hard-fail for any agent that has never had a main
  // session created.
  const candidates: string[] = [];
  if (resolvedMain) candidates.push(resolvedMain);
  if (agentId !== "main") candidates.push(`agent:main:main`);
  const globalMain = resolveMainSessionKey(host.hello, host.sessionsResult);
  if (globalMain && !candidates.includes(globalMain)) candidates.push(globalMain);
  const currentKey = host.sessionKey.trim();
  if (currentKey && !candidates.includes(currentKey)) candidates.push(currentKey);
  const label = nextChatTabLabel(getChatOpenTabs(host).length);
  let forked: SessionsForkResult | null = null;
  let lastError: string | null = null;
  for (const candidate of candidates) {
    const result = await forkSession(
      host as unknown as Parameters<typeof forkSession>[0],
      candidate,
      label,
      { copyTranscript: false },
    );
    if (result) {
      forked = result;
      break;
    }
    lastError = host.sessionsError;
  }
  if (!forked) {
    host.lastError = lastError ?? "Could not create a new session.";
    return;
  }
  // JAISIU-new-chat-must-fork: the fork already minted a fresh
  // `sessionKey` + `sessionId` with an empty transcript. Do NOT follow
  // up with a `/new` reset here — it would archive the transcript we
  // just created and, on the main session, preserve the `sessionId`
  // (GAAL-SESS-CACHE-001), which is the exact "stuck in the same chat"
  // symptom this fix removes.
  openChatTab(host, cache, forked.sessionKey);
}

/** Fork current session: copies transcript from the active tab's session. */
export async function handleForkCurrentChatTab(host: ChatTabsHost, cache: Map<string, DraftEntry>) {
  const source = host.sessionKey.trim();
  if (!source) {
    return;
  }
  // JAISIU-new-chat-must-fork: "Fork current" is an EXPLICIT request for
  // a separate chat. It used to take a guard branch that reset the main
  // session in place (same `sessionKey`), which meant the operator stayed
  // in the same chat. Fork it properly instead — `sessions.fork` mints a
  // new `sessionKey` with the transcript copied across.
  const label = nextChatTabLabel(getChatOpenTabs(host).length);
  const forked = await forkSession(
    host as unknown as Parameters<typeof forkSession>[0],
    source,
    label,
    {
      copyTranscript: true,
    },
  );
  if (!forked) {
    host.lastError = host.sessionsError ?? "Could not fork session.";
    return;
  }
  openChatTab(host, cache, forked.sessionKey);
}

/**
 * Reset the fleet's chat state to a single main tab:
 *   1. Resolve the "main" session key via resolveMainSessionKey (gateway
 *      snapshot / agent:…:main), then host.sessionKey — never `systemSent`
 *      (that flag means "prompt injected", not "is main").
 *   2. Delete every other open-tab session via sessions.delete (best-effort).
 *   3. Reset the main session via sessions.reset with confirm:true (best-effort).
 *   4. Clear draft cache entries for deleted keys.
 *   5. Replace chatOpenTabs with just [main].
 *
 * All RPC calls are best-effort: failures are swallowed so a partial reset still succeeds.
 */
export async function resetFleetChatTabs(
  host: ChatTabsHost,
  cache: Map<string, DraftEntry>,
): Promise<void> {
  const tabs = Array.isArray(host.settings?.chatOpenTabs) ? host.settings.chatOpenTabs : [];

  const main =
    resolveMainSessionKey(host.hello, host.sessionsResult)?.trim() ||
    (host.sessionKey ?? "").trim();

  // Collect non-main tabs to delete.
  const toDelete = tabs.filter((k) => k && k !== main);

  // Best-effort delete.
  for (const key of toDelete) {
    try {
      await host.client?.request("sessions.delete", {
        key,
        deleteTranscript: true,
      });
    } catch {
      /* swallow — partial reset is acceptable */
    }
    cache.delete(key);
    // Deleted sessions must not leave a resident transcript behind.
    host.chatMessagesBySession?.delete(key);
    void idbDeleteDraft(key);
  }

  // Best-effort reset main. Server requires confirm:true (JAISIU-2186);
  // without it the call is a no-op and the main transcript is left intact.
  if (main) {
    try {
      await host.client?.request("sessions.reset", { key: main, confirm: true });
    } catch {
      /* swallow */
    }
  }

  // Replace openTabs with just main.
  if (typeof host.applySettings === "function") {
    // JAISIU-types-sweep: `applySettings` REPLACES the whole settings
    // object (`app-settings.ts` assigns `host.settings = normalized`
    // and persists it), so the previous `{ chatOpenTabs }`-only
    // literal would have wiped every other setting — gatewayUrl, token,
    // sessionKey, theme, … Spreading `host.settings` matches every
    // other call site in this file and is what the code always meant.
    host.applySettings({ ...host.settings, chatOpenTabs: main ? [main] : [] });
  }
}
